import React, {useEffect} from "react";
import { Helmet } from "react-helmet-async";
import { baseImage_path } from "../variables/Variables";
import { stripHtmlForMeta } from "../utils/seoMetaText";

const SeoHelmet = ({ seoData }) => {
 
  const decodeHtmlEntities = (html) => {
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    return textarea.value;
  };

  const extractScriptContent = (htmlString) => {
    const decoded = decodeHtmlEntities(htmlString);

    const matches = decoded.match(/<script[^>]*type=['"]?(.*?)['"]?[^>]*>([\s\S]*?)<\/script>/i);

    if (matches) {
      return {
        type: matches[1] || "text/javascript",
        content: matches[2].trim(),
      };
    }

    // Fallback: assume JS
    return {
      type: "text/javascript",
      content: decoded.trim(),
    };
  };

  // ✅ Dynamically inject and execute custom script
  useEffect(() => {
    if (!seoData?.custom_scripts) return;

    const decodeHtmlEntities = (html) => {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = html;
      return textarea.value;
    };

    let decoded = decodeHtmlEntities(seoData.custom_scripts).trim();

    // Extract <script type="...">...</script>
    const match = decoded.match(
      /<script[^>]*type=["']?(.*?)["']?[^>]*>([\s\S]*?)<\/script>/i
    );

    let type = "text/javascript";
    let content = decoded;

    if (match) {
      type = match[1] || "text/javascript";
      content = match[2].trim();
    }

    // ✅ Fallback: raw JSON without script tag
    if (!match && decoded.startsWith("{") && decoded.endsWith("}")) {
      try {
        JSON.parse(decoded);
        type = "application/ld+json";
        content = decoded;
      } catch (err) {
        console.error("❌ Invalid fallback JSON-LD", err);
        return;
      }
    }

    // ✅ If content *looks* like JSON, use type=application/ld+json
    const isJsonLd =
      type === "application/ld+json" ||
      (content.trim().startsWith("{") && content.trim().endsWith("}"));

    if (isJsonLd) {
      try {
        JSON.parse(content); // verify it’s valid JSON
        type = "application/ld+json"; // override if needed
      } catch (err) {
        console.error("❌ Invalid JSON-LD", err);
        return;
      }
    } else {
      // ⛔ If content is JSON but type is JS, stop here
      if (content.trim().startsWith("{")) {
        console.error(
          "❌ Detected JSON but not using application/ld+json. Abort script injection."
        );
        return;
      }
    }

    const existing = document.getElementById("seo-script");
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.id = "seo-script";
    script.type = type;
    script.text = content;
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("seo-script");
      if (el) el.remove();
    };
  }, [seoData?.custom_scripts]);

  if (!seoData) return null;

  return (
    <Helmet key={seoData?.id}>
      <title>{seoData.seo_title}</title>
      <meta name="title" content={seoData.meta_title} />
      <meta name="description" content={stripHtmlForMeta(seoData.meta_description)} />
      <meta name="keywords" content={seoData.meta_keywords} />

      <meta property="og:title" content={seoData.og_title} />
      <meta property="og:description" content={stripHtmlForMeta(seoData.og_description)} />
      <meta property="og:type" content={seoData.og_type} />
      <meta property="og:url" content={seoData.og_url} />
      {seoData.og_image && (
        <meta
          property="og:image"
          content={`${baseImage_path}/${seoData.og_image}`}
        />
      )}
      <meta property="og:locale" content={seoData.og_locale} />
      <meta property="og:site_name" content={seoData.og_site_name} />
      <meta
        property="article:modified_time"
        content={seoData.article_modified_time}
      />

      <meta name="twitter:card" content={seoData.twitter_card} />
      <meta name="twitter:title" content={seoData.twitter_title} />
      <meta name="twitter:description" content={stripHtmlForMeta(seoData.twitter_description)} />
      {seoData.twitter_image && (
        <meta
          name="twitter:image"
          content={`${baseImage_path}/${seoData.twitter_image}`}
        />
      )}
      <meta name="twitter:site" content={seoData.twitter_site} />

      <link rel="canonical" href={seoData.canonical_link} />

	  {/*  {seoData.custom_scripts && (
        <script
          type="text/javascript"
          dangerouslySetInnerHTML={{ __html: seoData.custom_scripts }}
        />
      )} */}
    </Helmet>
  );
};

export default SeoHelmet;
